import React, { useEffect, useRef, useState } from "react";
import { siteData } from "../data/mock";

const useInView = (ref) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
};

const AnimatedNumber = ({ target }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1400;
    const startTime = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(start + (target - start) * eased));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target]);
  return (
    <span ref={ref} className="number-tick text-[64px] sm:text-[76px] md:text-[86px] leading-none">
      {val}
    </span>
  );
};

const Stats = () => {
  const { stats } = siteData;
  return (
    <section className="py-16 sm:py-20 lg:py-32 relative">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10">
        <div>
          <div className="flex items-center gap-3">
            <span className="eyebrow-line" />
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#5EC7F5]">
              {stats.eyebrow}
            </span>
            <span className="eyebrow-line" />
          </div>
          <h2 className="font-display text-[32px] sm:text-[44px] md:text-[52px] lg:text-[60px] leading-[1.02] sm:leading-[0.98] mt-6 text-gradient-title uppercase">
            {stats.title}
          </h2>
          <p className="font-italic-serif text-[20px] sm:text-[26px] md:text-[32px] lg:text-[36px] mt-2 text-gradient-blue">
            {stats.italic}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 mt-12 sm:mt-14">
          {stats.items.map((item, idx) => (
            <div
              key={idx}
              className="card-glow rounded-2xl p-6 sm:p-8 relative overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -top-16 -right-16 w-56 h-56 rounded-full"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(90,200,220,0.18), transparent 70%)",
                }}
              />
              <div className="flex items-baseline gap-2 flex-wrap">
                <AnimatedNumber target={item.value} />
                <span className="number-tick text-[36px] sm:text-[44px] md:text-[52px] leading-none">
                  {item.suffix}
                </span>
                {item.unit && (
                  <span className="font-italic-serif text-[22px] sm:text-[26px] text-gradient-blue ml-1">
                    {item.unit}
                  </span>
                )}
              </div>
              <div className="mt-4 text-[11px] tracking-[0.28em] sm:tracking-[0.32em] uppercase text-slate-300">
                {item.label}
              </div>
              <div className="mt-4 h-px divider-line" />
              <p className="text-slate-400 text-[14px] leading-relaxed mt-5">
                {item.desc}
              </p>
              <p className="mt-6 text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase text-[#5EC7F5]">
                Source · <span className="text-slate-300">{item.source}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
